import { Injectable, Injector } from '@angular/core';
import { AuthenticationInterface } from 'src/app/models/auth/authentication-interface';
import { UserModel } from 'src/app/models/auth/user-model';
import { WebService } from '../web/web.service';
import { CommonService } from '../common/common.service';
import { UndefinedNullableString} from 'src/app/type-alias';
import { GrantAccessModel } from 'src/app/models/auth/grant-access-model';
import { InstallerViewModel } from 'src/app/models/auth/installer-view-model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AppSessionStorageService {
  // private static readonly tokenKey: string = '1';
  private static readonly userKey: string = '2';
  private static readonly installerKey:string = '4'
  // private static readonly rememberMeKey: string = '3';

  /*
   * If the StorageService created as separate object, the [setUser] and [setToken] functions will not reflect other StorageService instances.\
   * So created these variable as static
   */
  public static auth: AuthenticationInterface | null = null;
  public static user: UserModel | null = null;
  public static installeView: InstallerViewModel | null = null;
  public static grantAccess: GrantAccessModel | null = null;
  private static initialized: boolean = false;
  private static isCallingRefreshToken = false;
  public static  verifiedInstallerList:any = []
  public static  unVerifiedInstallerList:any = []
  public static  adminDashboardVerifiedInstallerView:boolean = true
  public static searchFilter:string=''
  public static twoFactorToken:string=''
  public static twoFactorSlug:string=''
  public static sortFilter:string = ''
  public static sideBarSelection:number = 1
  public static registerUserType:string = ''
  public static appRedirctionLink:string = environment.app_redirection

  constructor(
    private injector: Injector,
    private commonService: CommonService
  ) {
    this.setup();
  }

  static isLoggedIn(): boolean {
    /// Need to implement JWT token expiry check
    return AppSessionStorageService.auth !== null;
  }

  private isRefreshTokenValid(token: AuthenticationInterface): boolean {
    return this.tokenExpireTime(token.refresh_token_expire_time) > 0;
  }

  async setup() {
    if (sessionStorage && !AppSessionStorageService.initialized) {
      try {
        const token = this.getToken();
        if (token) {
          this.checkToken(token);
        }
        const user = await this.getUser();
        if (user) {
          AppSessionStorageService.user = user;
        }
        AppSessionStorageService.initialized = true;
      } catch (_) {}
    }
  }

  private checkToken(token: AuthenticationInterface) {
    if (this.isRefreshTokenValid(token)) {
      this.setTokenToVariable(token);
    } else {
      this.removeToken();
    }
  }

  private setTokenToVariable(token: AuthenticationInterface) {
    AppSessionStorageService.auth = token;
    this.checkAccessTokenExpireTime();
    this.refreshTheToken();
  }

  private refreshTheToken() {
    if (this.shouldRememberToken()) {
      try {
        ///Refresh the token

        const webService = this.injector.get(WebService);
        webService.refreshTheToken();
      } catch (_) {}
    }
  }

  private async setModel(model: any, key: string): Promise<void> {
    try {
      if (sessionStorage) {
        if (model === null || model === undefined) {
          sessionStorage.removeItem(key);
        } else {
          const encoded = await this.commonService.encrypt(
            JSON.stringify(model)
          );
          sessionStorage.setItem(key, encoded);
        }
      }
    } catch (_) {}
  }

  private async getModel(key: string): Promise<any> {
    try {
      if (sessionStorage) {
        const data = sessionStorage.getItem(key);
        if (data === null || data === undefined) {
          return null;
        }
        const decoded = await this.commonService.decryptData(data);
        return JSON.parse(decoded);
      }
    } catch (_) {}
    return null;
  }

  private getString(key: string): string | undefined | null {
    try {
      if (sessionStorage) {
        return sessionStorage.getItem(key);
      }
    } catch (_) {}
    return null;
  }

  private removeKey(key: string) {
    try {
      if (sessionStorage) {
        sessionStorage.removeItem(key);
      }
    } catch (_) {}
  }

  shouldRememberToken(): boolean {
    return true;
  }

  tokenExpireTime(token: number | undefined | null): number {
    try {
      if (token) {
        const time = token - Date.now();
        return isNaN(time) || time < 1 ? 0 : time;
      }
    } catch (_) {}

    return 0;
  }

  private async checkAccessTokenExpireTime() {
    if (!AppSessionStorageService.isCallingRefreshToken) {
      try {
        AppSessionStorageService.isCallingRefreshToken = true;
        if (AppSessionStorageService.auth) {
          const time = this.tokenExpireTime(
            AppSessionStorageService.auth.access_token_expire_time
          );
          this.checkIsAccessTokenExpireTime(time);
        }
      } catch (_) {}
      AppSessionStorageService.isCallingRefreshToken = false;
    }
  }

  private async checkIsAccessTokenExpireTime(time: number) {
    const maxRetryTime = 60000;
    if (
      AppSessionStorageService.auth &&
      ((time > 0 && time < 120000) ||
        (time == 0 &&
          this.tokenExpireTime(
            AppSessionStorageService.auth.refresh_token_expire_time
          ) > 0))
    ) {
      const webService = this.injector.get(WebService);
      await webService.refreshTheToken();
      setTimeout(() => {
        this.checkAccessTokenExpireTime();
      }, maxRetryTime);
    } else if (time > 0) {
      /// Recheck after 1 minute
      setTimeout(() => {
        this.checkAccessTokenExpireTime();
      }, maxRetryTime);
    }
  }

  private async checkGrantAccessTokenExpireTime() {
    if (!AppSessionStorageService.isCallingRefreshToken) {
      try {
        AppSessionStorageService.isCallingRefreshToken = true;
        if (AppSessionStorageService.grantAccess) {
          const time = this.tokenExpireTime(
            AppSessionStorageService.grantAccess.access_token_expire_time
          );
          this.checkIsAccessTokenExpireTime(time);
        }
      } catch (_) {}
      AppSessionStorageService.isCallingRefreshToken = false;
    }
  }


  private async checkIsGrantAccessTokenExpireTime(time: number) {
    const maxRetryTime = 60000;
    if (
      AppSessionStorageService.grantAccess &&
      ((time > 0 && time < 120000) ||
        (time == 0 &&
          this.tokenExpireTime(
            AppSessionStorageService.grantAccess.refresh_token_expire_time
          ) > 0))
    ) {
      const webService = this.injector.get(WebService);
      await webService.refreshTheToken();
      setTimeout(() => {
        this.checkAccessTokenExpireTime();
      }, maxRetryTime);
    } else if (time > 0) {
      /// Recheck after 1 minute
      setTimeout(() => {
        this.checkAccessTokenExpireTime();
      }, maxRetryTime);
    }
  }

  async setToken(model: AuthenticationInterface | null, remember: boolean): Promise<void> {
    try {
      AppSessionStorageService.auth = model;
      if (sessionStorage) {
        if (remember && model !== null) {
          await this.checkAccessTokenExpireTime();
        }
      }
    } catch (_) {}
  }

  getToken(): AuthenticationInterface | null {
    return AppSessionStorageService.auth;
    /// The sessionStorage may used directly on any files. When we are not used this class to set data, the varibale will null
  }

  async setGrantToken(model: GrantAccessModel | null, remember: boolean): Promise<void> {
    try {
      AppSessionStorageService.grantAccess = model;
      if (sessionStorage) {
        if (remember && model !== null) {
          await this.checkGrantAccessTokenExpireTime();
        }
      }
    } catch (_) {}
  }

  getGrantToken(): GrantAccessModel | null {
    return AppSessionStorageService.grantAccess;
    /// The sessionStorage may used directly on any files. When we are not used this class to set data, the varibale will null
  }

  async setUser(model: UserModel | null): Promise<void> {
    /// The sessionStorage may used directly on any files. When we are not used this class to set data, the varibale will null
    AppSessionStorageService.user = model;
    this.setModel(model, AppSessionStorageService.userKey);
  }

  async getUser(): Promise<UserModel | null> {
    return this.getModel(AppSessionStorageService.userKey);
  }

  async setInstallerViewUser(model: InstallerViewModel | null): Promise<void> {
    /// The sessionStorage may used directly on any files. When we are not used this class to set data, the varibale will null
    AppSessionStorageService.installeView = model;
    this.setModel(model, AppSessionStorageService.installerKey);
  }

  async getInstallerViewUser(): Promise<InstallerViewModel | null> {
    return this.getModel(AppSessionStorageService.installerKey);
  }

  removeToken() {
    AppSessionStorageService.auth = null;
    this.removeUser();
    AppSessionStorageService.isCallingRefreshToken = false;
    try {
      this.getCommonService()?.notifyLogout();
    } catch (_) {}
  }

  removeUser() {
    AppSessionStorageService.user = null;
    this.removeKey(AppSessionStorageService.userKey);
  }

  getCommonService(): CommonService | null {
    try {
      return this.injector.get(CommonService);
    } catch (_) {}
    return null;
  }

  async onEncryptionKeyUpdated(key: UndefinedNullableString) {}
}
